<template>
    <!-- Modal for Ticket View -->
    <div v-if="showTicketModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <!-- Background overlay -->
            <div class="fixed inset-0 transition-opacity" aria-hidden="true">
                <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closeModal"></div>
            </div>

            <!-- Modal content -->
            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <!-- Close button -->
                <div class="absolute top-0 right-0 pt-4 pr-4">
                    <button @click="closeModal" class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none">
                        <span class="sr-only">Close</span>
                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Ticket content -->

                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                    <div v-if="loadingFailed" class="flex flex-col items-center justify-center p-4">
                        <div class="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
                            <svg class="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                            <h2 class="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h2>
                            <p class="text-gray-600 mb-6">We couldn't load the ticket information.</p>
                            <button @click="closeModal" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                Close
                            </button>
                        </div>
                    </div>

                    <div v-else class="space-y-6">
                        <!-- Closed Ticket Banner -->
                        <div v-if="ticket.closed" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <p class="text-sm text-red-700">
                                        This ticket was closed
                                        <span v-if="ticket.closed_at"> on {{ moment(ticket.closed_at).format('LLL') }}</span>
                                        <span v-if="ticket.closed_by"> by {{ ticket.closed_by?.name || 'System' }}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Top Section - Reference and Status -->
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                            <div class="md:col-span-2">
                                <p class="font-semibold text-gray-700 text-lg mb-1">Reference Number:</p>
                                <p class="text-2xl font-bold text-blue-700 tracking-wide">{{ ticket.uid }}</p>
                            </div>
                            <div class="md:text-left">
                                <p class="font-semibold text-gray-700 text-lg mb-1">Status:</p>
                                <span :class="getStatusClass(ticket.status.name)" class="inline-flex items-center px-4 py-2 rounded-full text-base font-semibold shadow-sm">
                                    {{ ticket.status.name }}
                                </span>
                            </div>
                        </div>

                        <!-- Subject Section -->
                        <div>
                            <p class="font-semibold text-gray-700 text-lg mb-1">Subject:</p>
                            <p class="text-gray-800 text-lg leading-relaxed">{{ ticket.subject }}</p>
                        </div>

                        <!-- Date Information -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p class="font-semibold text-gray-700 text-lg mb-1">Submitted On:</p>
                                <p class="text-gray-800 text-base">{{ formatDateTime(ticket.created_at) }}</p>
                            </div>
                            <div v-if="ticket.closed">
                                <p class="font-semibold text-gray-700 text-lg mb-1">Closed On:</p>
                                <p class="text-gray-800 text-base">{{ formatDateTime(ticket.closed_at) }}</p>
                            </div>
                        </div>

                        <!-- Details Section -->
                        <div>
                            <p class="font-semibold text-gray-700 text-lg mb-2">Details:</p>
                            <div class="prose max-w-none text-gray-800 text-base leading-relaxed p-4 bg-gray-50 rounded-lg border border-gray-200" v-html="ticket.details"></div>
                        </div>

                        <!-- Attachments Section -->
                        <div v-if="ticket.attachments && ticket.attachments.length > 0">
                            <p class="font-semibold text-gray-700 text-lg mb-2">Attachments:</p>
                            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                <div v-for="attachment in ticket.attachments" :key="attachment.id" class="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                                    <a :href="`/storage/${attachment.path}`" target="_blank" class="flex items-center text-blue-600 hover:text-blue-800 transition duration-200">
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                        </svg>
                                        <span class="truncate">{{ attachment.name }}</span>
                                    </a>
                                    <p class="text-xs text-gray-500 mt-1">{{ formatFileSize(attachment.size) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Review Section -->
                        <div v-if="ticket.closed" class="mt-6 border-t border-gray-200 pt-6">
                            <h3 class="text-lg font-medium text-gray-900 mb-4">Kindly review our service</h3>

                            <!-- Review Form -->
                            <div v-if="!ticket.review" class="bg-gray-50 p-6 rounded-lg">
                                <form @submit.prevent="submitReview">
                                    <div class="mb-4">
                                        <label class="block text-gray-700 text-sm font-medium mb-2">Rating</label>
                                        <div class="flex items-center space-x-2">
                                            <button v-for="star in 5" :key="star" type="button" @click="review.rating = star" class="focus:outline-none">
                                                <svg class="w-8 h-8" :class="star <= review.rating ? 'text-yellow-400' : 'text-gray-300'" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    <div class="mb-4">
                                        <label for="comment" class="block text-gray-700 text-sm font-medium mb-2">Your Feedback</label>
                                        <textarea id="comment" v-model="review.comment" rows="4" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="How was your experience with our service?"></textarea>
                                    </div>

                                      <LoadingButton type="submit" :disabled="reviewForm.processing"
                                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
                                        <span v-if="!reviewForm.processing">Submit record</span>
                                        <span v-else>Submitting...</span>
                                    </LoadingButton>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button type="button" @click="closeModal" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import moment from 'moment'
import LoadingButton from '@/Shared/LoadingButton'

export default {
    components: {
        Head,
        Link,
        LoadingButton
    },
    props: {
        ticket: Object,
        statuses: Array,
        suggestions: Array,
        showTicketModal: Boolean
    },
    data() {
        return {
            loadingFailed: false,
            review: {
                rating: 0,
                comment: '',
                ticket_id: this.ticket?.id || null
            },
            newSuggestion: ''
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        submitReview() {
            if (!this.review.rating) {
                alert('Please select a rating');
                return;
            }

            this.$inertia.post(`/tickets/${this.ticket.id}/review`, this.review, {
                preserveScroll: true,
                onSuccess: () => {
                    this.$toast.success('Thank you for your review!');
                    this.review = { rating: 0, comment: '' };
                }
            });
        },
        submitSuggestion() {
            if (!this.newSuggestion.trim()) return;

            this.$inertia.post(`/tickets/${this.ticket.id}/suggestions`, {
                content: this.newSuggestion
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    this.newSuggestion = '';
                    this.$toast.success('Suggestion submitted successfully');
                }
            });
        },
        formatDateTime(dateString) {
            if (!dateString) return '';
            return moment(dateString).format('LLL');
        },
        moment(date) {
            return moment(date);
        },
        getStatusClass(statusName) {
            const normalizedStatus = statusName.toLowerCase();
            switch (normalizedStatus) {
                case 'pending':
                    return 'bg-yellow-100 text-yellow-800';
                case 'resolved':
                    return 'bg-green-100 text-green-800';
                case 'closed':
                    return 'bg-gray-200 text-gray-700';
                case 'open':
                    return 'bg-blue-100 text-blue-800';
                default:
                    return 'bg-purple-100 text-purple-800';
            }
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]);
        }
    }
}
</script>
